import { useState } from "react";
import {
  Stack,
  Box,
  Flex,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Badge,
  Card,
  CardBody,
  CardFooter,
  Heading,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  Select,
  Icon,
  Image,
  AspectRatio,
} from "@chakra-ui/react";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { FiSearch, FiPlus, FiPlay, FiClock, FiEye, FiEdit, FiTrash2 } from "react-icons/fi";

const TrainingVideos = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchInput, setSearchInput] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  // Mock data
  const videos = [
    {
      id: "1",
      title: "Platform Overview",
      description: "A complete walkthrough of the platform features and navigation.",
      category: "Getting Started",
      duration: "15:30",
      views: 2450,
      thumbnail: "https://via.placeholder.com/320x180?text=Platform+Overview",
      uploadedAt: "2024-02-15",
      isPublished: true,
    },
    {
      id: "2",
      title: "Booking Management Tutorial",
      description: "Learn how to manage bookings effectively from creation to completion.",
      category: "Operations",
      duration: "22:45",
      views: 1890,
      thumbnail: "https://via.placeholder.com/320x180?text=Booking+Management",
      uploadedAt: "2024-02-10",
      isPublished: true,
    },
    {
      id: "3",
      title: "Driver Verification Process",
      description: "Step-by-step guide for verifying driver documents and profiles.",
      category: "Verification",
      duration: "12:20",
      views: 1256,
      thumbnail: "https://via.placeholder.com/320x180?text=Driver+Verification",
      uploadedAt: "2024-02-08",
      isPublished: true,
    },
    {
      id: "4",
      title: "Finance Module Deep Dive",
      description: "Understanding the finance module, invoicing, and payment tracking.",
      category: "Finance",
      duration: "28:15",
      views: 876,
      thumbnail: "https://via.placeholder.com/320x180?text=Finance+Module",
      uploadedAt: "2024-02-05",
      isPublished: false,
    },
  ];

  const categories = ["all", "Getting Started", "Operations", "Verification", "Finance", "Support"];

  const categoryColors = {
    "Getting Started": "green",
    "Operations": "blue",
    "Verification": "purple",
    "Finance": "orange",
    "Support": "red",
  };

  const filteredVideos = videos.filter(video => {
    const matchesCategory = categoryFilter === "all" || video.category === categoryFilter;
    const matchesSearch = !searchInput || 
      video.title.toLowerCase().includes(searchInput.toLowerCase()) ||
      video.description.toLowerCase().includes(searchInput.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <Stack p={6} gap={4}>
      <BreadCrumbMaker
        items={[
          { link: "/support", title: "Support Center" },
          { link: "#", title: "Training Videos", on: true },
        ]}
      />

      <Box bg="white" borderRadius="xl" p={4} shadow="sm">
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontSize="xl" fontWeight="bold" color="gray.700">
            Training Videos
          </Text>
          <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={onOpen}>
            Add Video
          </Button>
        </Flex>

        <Flex gap={4} mb={6}>
          <InputGroup maxW="400px">
            <InputLeftElement>
              <FiSearch color="gray" />
            </InputLeftElement>
            <Input
              placeholder="Search videos..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              borderRadius="lg"
            />
          </InputGroup>
          <Select
            maxW="200px"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {cat === "all" ? "All Categories" : cat}
              </option>
            ))}
          </Select>
        </Flex>

        {filteredVideos.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Text color="gray.500">No videos found</Text>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={4}>
            {filteredVideos.map((video) => (
              <Card key={video.id} variant="outline" _hover={{ shadow: "md" }} overflow="hidden">
                <Box position="relative">
                  <AspectRatio ratio={16 / 9}>
                    <Image
                      src={video.thumbnail}
                      alt={video.title}
                      objectFit="cover"
                      bg="gray.100"
                    />
                  </AspectRatio>
                  <Flex
                    position="absolute"
                    bottom={2}
                    right={2}
                    bg="blackAlpha.700"
                    color="white"
                    px={2}
                    py={1}
                    borderRadius="md"
                    fontSize="xs"
                    align="center"
                    gap={1}
                  >
                    <FiClock />
                    <Text>{video.duration}</Text>
                  </Flex>
                  <Flex
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    bg="whiteAlpha.900"
                    borderRadius="full"
                    p={3}
                    cursor="pointer"
                    _hover={{ bg: "white" }}
                  >
                    <Icon as={FiPlay} boxSize={6} color="blue.500" />
                  </Flex>
                </Box>
                <CardBody py={3}>
                  <Flex justify="space-between" align="flex-start" mb={2}>
                    <Badge colorScheme={categoryColors[video.category] || "gray"} fontSize="xs">
                      {video.category}
                    </Badge>
                    <Badge colorScheme={video.isPublished ? "green" : "gray"} fontSize="xs">
                      {video.isPublished ? "Published" : "Draft"}
                    </Badge>
                  </Flex>
                  <Heading size="sm" mb={1} noOfLines={1}>{video.title}</Heading>
                  <Text fontSize="xs" color="gray.600" noOfLines={2}>
                    {video.description}
                  </Text>
                  <Flex mt={2} gap={4} fontSize="xs" color="gray.500">
                    <Flex align="center" gap={1}>
                      <FiEye />
                      <Text>{video.views} views</Text>
                    </Flex>
                  </Flex>
                </CardBody>
                <CardFooter pt={0} pb={3}>
                  <Flex gap={2}>
                    <Button size="xs" leftIcon={<FiPlay />} colorScheme="blue">
                      Play
                    </Button>
                    <Button size="xs" leftIcon={<FiEdit />} variant="outline">
                      Edit
                    </Button>
                    <Button size="xs" leftIcon={<FiTrash2 />} variant="outline" colorScheme="red">
                      Delete
                    </Button>
                  </Flex>
                </CardFooter>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Box>

      {/* Add Video Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Training Video</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Title</FormLabel>
                <Input placeholder="Enter video title" />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Description</FormLabel>
                <Textarea placeholder="Brief description of the video" rows={3} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Video URL</FormLabel>
                <Input placeholder="Enter video URL or upload" />
              </FormControl>
              <FormControl>
                <FormLabel>Thumbnail URL</FormLabel>
                <Input placeholder="Enter thumbnail URL or upload" />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Category</FormLabel>
                <Select placeholder="Select category">
                  <option value="Getting Started">Getting Started</option>
                  <option value="Operations">Operations</option>
                  <option value="Verification">Verification</option>
                  <option value="Finance">Finance</option>
                  <option value="Support">Support</option>
                </Select>
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue">Save as Draft</Button>
            <Button colorScheme="green" ml={2}>Publish</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

export default TrainingVideos;
